import OcrRecognizePreview from '@euler/app/flows/common/components/OcrRecognizePreview';
import { VinRecognizeResultView } from '@euler/app/flows/common/components/VinRecognizeResultView';
import { useOcrRecognize } from '@euler/app/flows/common/functions/useOcrRecognize';
import { OrderExtra } from '@euler/app/flows/order/types';
import { LayoutProviderView } from '@euler/components/layout/LayoutProvider';
import { wrapNavigatorScreen } from '@euler/functions/wrapNavigatorScreen';
import { ResourceAccessScope } from '@euler/model/enum';
import { useServiceFactory } from '@euler/services/factory';
import { VehicleServiceInfo } from '@euler/services/vehicle-info.service';
import { useNavigation } from '@react-navigation/native';
import {
  CardStyleInterpolators,
  StackNavigationOptions,
} from '@react-navigation/stack';
import { useCallback, useState } from 'react';
import { BehaviorSubject } from 'rxjs';

export const VinRecognizeResultScreen = wrapNavigatorScreen(
  ({
    plateNo,
    imageUri,
    originalImageUri,
    simulatedValue,
    orderDetail$,
    onConfirm,
  }: {
    plateNo?: string;
    imageUri: string;
    originalImageUri: string;
    simulatedValue?: string;
    orderDetail$: BehaviorSubject<OrderExtra>;
    onConfirm?: (
      plateNo: string,
      imageUrl: string | undefined,
      serviceInfo?: VehicleServiceInfo,
    ) => void;
  }) => {
    const { ocrService, vehicleInfoService } = useServiceFactory();
    const [lastResult, setLastResult] = useState<string>();
    const {
      recognizeState,
      startRecognize,
      extraInfoState,
      fetchExtraInfo,
      recognizedResponse,
    } = useOcrRecognize({
      imageUri,
      recognize: async (uri: string) => {
        __DEV__ && console.log('recognizing ' + uri + '...');
        if (simulatedValue) {
          return {
            result: simulatedValue,
          };
        }
        return await ocrService.vinRecognize({
          uri,
        });
      },
      fetchExtraInfo: async result => {
        const res = await vehicleInfoService.listVehicleServiceInfo({
          scope: ResourceAccessScope.Org,
          plateno: plateNo,
          vin: result,
          projection: { pendingIssues: true },
        });
        if (result !== lastResult) {
          setLastResult(result);
        }
        return res;
      },
    });

    const navigation = useNavigation();
    const onConfirmPress = useCallback(
      (vin: string, serviceInfo?: VehicleServiceInfo) => {
        navigation.goBack();
        const imageUrl =
          lastResult !== recognizedResponse?.result
            ? undefined
            : recognizedResponse?.url;
        onConfirm?.(vin, imageUrl, serviceInfo);
      },
      [
        lastResult,
        navigation,
        onConfirm,
        recognizedResponse?.result,
        recognizedResponse?.url,
      ],
    );

    return (
      <OcrRecognizePreview
        label="车牌"
        state={recognizeState}
        imageUri={originalImageUri}
        onRetry={startRecognize}
      >
        {result => (
          <LayoutProviderView
            fallbackToChildren
            css={`
              flex: 1;
            `}
          >
            <VinRecognizeResultView
              result={result}
              orderDetail$={orderDetail$}
              extraInfo={extraInfoState}
              fetchExtraInfo={fetchExtraInfo}
              lastResult={lastResult}
              onConfirm={onConfirmPress}
            />
          </LayoutProviderView>
        )}
      </OcrRecognizePreview>
    );
  },
  {
    presentation: 'transparentModal',
    headerShown: false,
    cardStyleInterpolator: CardStyleInterpolators.forFadeFromCenter,
    detachPreviousScreen: false,
    cardOverlayEnabled: true,
    cardStyle: {
      backgroundColor: '#fff',
      flex: 1,
    },
  } as StackNavigationOptions,
);
